import { server_error_message, server_error_title } from "../utilities/values";
import { baseUrl, forgerockErrors } from "../utilities/configurations";
import { FRAuth, StepType } from "@forgerock/javascript-sdk";
import { useNavigate } from "react-router";
import { useAuth } from "./AuthContext";
import PropTypes from "prop-types";
import axios from "axios";
import {
  validateMobileNumber,
  startJourney,
  resetConfig,
} from "../utilities/helper";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const LoginContext = createContext();

export function useLoginContext() {
  return useContext(LoginContext);
}

export function LoginProvider(props) {
  const [displayIsTCRequired, setDisplayIsTCRequired] = useState(false);
  const [showExpiredOtpModal, setShowExpiredOtpModal] = useState(false);
  const { setIsAuthenticated, setAuthenticatedFrom } = useAuth();
  const [configRestarted, setConfigRestarted] = useState(false);
  const [displayAlert, setDisplayAlert] = useState(false);
  const [journeyStep, setJourneyStep] = useState(null);
  const [mobileNumber, setMobileNumber] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [isTermsID, setIsTermsID] = useState("");
  const [userId, setUserId] = useState("");
  const [mpOtp, setMpOtp] = useState("");
  const [page, setPage] = useState(1);
  const [otp, setOtp] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (configRestarted) {
      resetConfig();
      setConfigRestarted(false);
      init();
    }
  }, [configRestarted]);

  const init = async () => {
    startJourney(process.env.REACT_APP_JOURNEY_LOGIN);
    setJourneyStep(null);
    try {
      const initialStep = await FRAuth.start();
      setJourneyStep(initialStep);
    } catch (err) {
      setAlertMessage(
        "Login initialization was unsuccessful. Please refresh the page."
      );
    }
  };

  const resetValues = () => {
    setOtp("");
    setMobileNumber("");
  };

  const formatMobileNumber = (e) => {
    const inputVal = e.target.value.replace(/ /g, "");
    let inputNumbersOnly = inputVal.replace(/\D/g, "");

    if (inputNumbersOnly.length > 10) {
      inputNumbersOnly = inputNumbersOnly.substr(0, 10);
    }

    setMobileNumber(inputNumbersOnly);
    setAlertMessage("");
  };

  const handleNext = async (e) => {
    try {
      e.preventDefault();
    } catch (error) {
      console.log(error);
    }

    let str = `+63${mobileNumber}`;

    setAlertMessage("");
    if (!validateMobileNumber(str)) {
      setAlertMessage("Please enter a valid mobile number");
    } else {
      setIsLoading(true);
      let journey_obj = JSON.parse(JSON.stringify(journeyStep));
      journey_obj.callbacks[0].payload.input[0].value = str;
      journey_obj.payload.callbacks[0].input[0].value = str;
      try {
        const nextStep = await FRAuth.next(journey_obj);
        switch (nextStep.type) {
          case StepType.Step:
            setJourneyStep(nextStep);
            navigate("/login/verify-otp");
            break;
          case StepType.LoginFailure:
            {
              const detail = nextStep.getDetail();
              const errorReason = nextStep.getReason();
              const errorMessage = nextStep.getMessage();
              if (detail?.errorCode === "110") {
                setDisplayAlert(true);
                setAlertTitle(errorReason);
                setAlertMessage(errorMessage);
              } else {
                setAlertMessage(
                  forgerockErrors[detail?.failureUrl] ||
                    "Something went wrong. Please try again."
                );
              }
              setConfigRestarted(true);
            }
            break;
        }
      } catch (err) {
        setConfigRestarted(true);
        setDisplayAlert(true);
        setAlertTitle(server_error_title);
        setAlertMessage(server_error_message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubmitChoice = async (step3) => {
    let journey_obj = JSON.parse(JSON.stringify(step3));
    journey_obj.callbacks[0].payload.input[0].value = "0";
    journey_obj.payload.callbacks[0].input[0].value = "0";
    try {
      const nextStep = await FRAuth.next(journey_obj);
      switch (nextStep.type) {
        case StepType.LoginSuccess:
          setIsAuthenticated(true);
          setAuthenticatedFrom("login");
          break;
        case StepType.LoginFailure:
          {
            const detail = nextStep.getDetail();
            const errorReason = nextStep.getReason();
            const errorMessage = nextStep.getMessage();
            if (detail?.errorCode === "110") {
              setDisplayAlert(true);
              setAlertTitle(errorReason);
              setAlertMessage(errorMessage);
            } else {
              setDisplayAlert(true);
              setAlertTitle("Error");
              setAlertMessage(
                forgerockErrors[detail?.failureUrl] || forgerockErrors["SWW"]
              );
            }
            resetValues();
            setConfigRestarted(true);
            navigate("/login", { replace: true });
            setIsLoading(false);
          }
          break;
      }
    } catch (err) {
      setDisplayAlert(true);
      setAlertTitle(server_error_title);
      setAlertMessage(server_error_message);
      resetValues();
      setConfigRestarted(true);
      navigate("/login", { replace: true });
      setIsLoading(false);
      console.log(err);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    let journey_obj = JSON.parse(JSON.stringify(journeyStep));
    journey_obj.callbacks[0].payload.input[0].value = otp;
    journey_obj.payload.callbacks[0].input[0].value = otp;
    try {
      const nextStep = await FRAuth.next(journey_obj);
      switch (nextStep.type) {
        case StepType.Step:
          if (nextStep.payload.callbacks[0].type === "PasswordCallback") {
            setAlertMessage("Oops! Incorrect OTP. Please try again");
            setIsLoading(false);
          } else if (
            nextStep.payload.callbacks[0].type === "TextOutputCallback" &&
            nextStep.payload.callbacks[0].output[0].value === "OTP is Expired"
          ) {
            setShowExpiredOtpModal(true);
            setIsLoading(false);
          } else if (nextStep.payload.callbacks[0].type === "ChoiceCallback") {
            handleSubmitChoice(nextStep);
          }
          setJourneyStep(nextStep);
          break;
        case StepType.LoginFailure:
          {
            const detail = nextStep.getDetail();
            const errorReason = nextStep.getReason();
            const errorMessage = nextStep.getMessage();
            if (detail?.errorCode === "110") {
              setDisplayAlert(true);
              setAlertTitle(errorReason);
              setAlertMessage(errorMessage);
            } else {
              setDisplayAlert(true);
              setAlertTitle("Error");
              setAlertMessage(
                forgerockErrors[detail?.failureUrl] || forgerockErrors["SWW"]
              );
            }
            resetValues();
            setConfigRestarted(true);
            navigate("/login", { replace: true });
            setIsLoading(false);
          }
          break;
      }
    } catch (err) {
      setDisplayAlert(true);
      setAlertTitle(server_error_title);
      setAlertMessage(server_error_message);
      resetValues();
      setConfigRestarted(true);
      navigate("/login", { replace: true });
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setMobileNumber("");
    setAlertMessage("");
    navigate("/");
    setOtp("");
  };

  const handleSignOut = async (e) => {
    try {
      await axios
        .post(baseUrl + `/logout`, {
          withCredentials: true,
        })
        .then(async () => {
          await axios.post(
            baseUrl + "/connect",
            {
              username: process.env.REACT_APP_CONNECT_USERNAME,
              password: process.env.REACT_APP_CONNECT_PASSWORD,
            },
            {
              headers: { "content-type": "application/json" },
              withCredentials: true,
            }
          );
        });
    } catch (error) {
      console.log(error);
    } finally {
      setDisplayIsTCRequired(false);
      setMobileNumber("");
      setOtp("");
      navigate("/", { replace: true });
    }
  };

  const data = useMemo(
    () => ({
      setShowExpiredOtpModal,
      setDisplayIsTCRequired,
      setConfigRestarted,
      formatMobileNumber,
      setAlertMessage,
      setMobileNumber,
      setDisplayAlert,
      setJourneyStep,
      setAlertTitle,
      handleSignOut,
      handleSubmit,
      setIsTermsID,
      resetValues,
      handleBack,
      handleNext,
      setUserId,
      setMpOtp,
      navigate,
      setPage,
      setOtp,

      showExpiredOtpModal,
      displayIsTCRequired,
      configRestarted,
      displayAlert,
      mobileNumber,
      alertMessage,
      journeyStep,
      alertTitle,
      isLoading,
      isTermsID,
      userId,
      mpOtp,
      page,
      otp,
    }),
    [
      showExpiredOtpModal,
      displayIsTCRequired,
      configRestarted,
      displayAlert,
      mobileNumber,
      alertMessage,
      journeyStep,
      alertTitle,
      isLoading,
      isTermsID,
      userId,
      mpOtp,
      page,
      otp,
    ]
  );

  return (
    <LoginContext.Provider value={data}>{props.children}</LoginContext.Provider>
  );
}
LoginProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
